/**
 * The single home of the fr-FR formatting conventions (enforced by lint:
 * `Intl` and `toLocale*` are banned everywhere else).
 *
 * Money travels in CENTS through the API — `formatPrice` owns the division.
 * The billing reads are the one exception: their amounts are converted to
 * euros server-side (`toEuros` at the use-case boundary) → `formatEuros`.
 */

const EUR = new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: 'EUR',
});

/** Cents → « 12,34 € ». */
export function formatPrice(cents: number): string {
  return EUR.format(cents / 100);
}

/** Euros → « 12,34 € » — billing reads only (server-side euros). */
export function formatEuros(euros: number): string {
  return EUR.format(euros);
}

const NUMBER_FR = new Intl.NumberFormat('fr-FR');

/** « 12 345 » — thousands separator, no decimals forced. */
export function formatNumber(value: number): string {
  return NUMBER_FR.format(value);
}

const DECIMAL_FR = new Intl.NumberFormat('fr-FR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** « 12 345,68 » — always two decimals. */
export function formatDecimal(value: number): string {
  return DECIMAL_FR.format(value);
}

const GO_FR = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 1 });

/** Mo → « 12,5 Go ». */
export function formatDataAllowance(mo: number): string {
  return `${GO_FR.format(mo / 1000)} Go`;
}

export function formatDate(date: string | Date | undefined) {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/** « 05/03/2025 ». */
export function formatDateShort(date: string | Date | undefined): string {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/** « 05/03 ». */
export function formatDayMonth(date: string | Date): string {
  return new Date(date).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
  });
}

/** « mars 2025 » (month is 1-based). */
export function formatMonthYear(month: number, year: number): string {
  return new Date(year, month - 1, 1).toLocaleDateString('fr-FR', {
    month: 'long',
    year: 'numeric',
  });
}

/** « janv. 26 » — compact axis label (month is 1-based). */
export function formatMonthYearShort(month: number, year: number): string {
  return new Date(year, month - 1, 1).toLocaleDateString('fr-FR', {
    month: 'short',
    year: '2-digit',
  });
}

/** « 14:05 ». */
export function formatTime(date: string | Date): string {
  return new Date(date).toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** « 05/03/2025 14:05 » — compact datetime for monitoring rows. */
export function formatDateTime(date: string | Date): string {
  return new Date(date).toLocaleString('fr-FR', {
    dateStyle: 'short',
    timeStyle: 'short',
  });
}

/** Local-timezone « YYYY-MM-DD » key (NOT the UTC slice of toISOString). */
export function localDateKey(date: string | Date): string {
  const d = new Date(date);
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${month}-${day}`;
}

// Past 60 minutes a raw minute count stops being readable, so switch to hours.
// Returns the label too, since it has to follow the unit actually displayed.
export function formatCallDuration(minutes: number): {
  value: string;
  label: string;
} {
  if (minutes < 60) return { value: String(minutes), label: "minutes d'appel" };
  const hours = Math.floor(minutes / 60);
  const remaining = minutes % 60;
  if (remaining > 0)
    return {
      value: `${hours} h ${String(remaining).padStart(2, '0')}`,
      label: "temps d'appel",
    };
  return { value: `${hours} h`, label: "temps d'appel" };
}

export function timeSinceActivation(dateString: string | undefined): string {
  if (!dateString) return '-';
  const activation = new Date(dateString);
  const now = new Date();
  const months =
    (now.getFullYear() - activation.getFullYear()) * 12 +
    (now.getMonth() - activation.getMonth());
  if (months < 1) return "Moins d'un mois";
  if (months < 12) return `${months} mois`;
  const years = Math.floor(months / 12);
  const remaining = months % 12;
  if (remaining > 0)
    return `${years} an${years > 1 ? 's' : ''} et ${remaining} mois`;
  return `${years} an${years > 1 ? 's' : ''}`;
}
